/**
 * Build column defs with `description` set for native DataGrid header tooltips:
 * header name, then Avg / Med lines (comps only, excludes subject row).
 */

const STATS_SKIP_FIELDS = new Set(['actions', 'parcel_id', 'pid', 'PhotoURL']);

function shouldIncludeRowForStats(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.isSubject) return false;
  return true;
}

function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string') {
    const stripped = value.replace(/,/g, '').replace(/\*/g, '').trim();
    if (stripped === '') return null;
    const n = Number(stripped);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function getValueForStats(row, colDef) {
  const raw = row[colDef.field];
  if (typeof colDef.valueGetter === 'function') {
    try {
      const got = colDef.valueGetter(raw, row, colDef, { current: null });
      if (got == null) return null;
      const t = typeof got;
      if (t === 'number' || t === 'string' || t === 'bigint') return got;
      return null;
    } catch {
      return raw;
    }
  }
  return raw;
}

function median(sortedNums) {
  const n = sortedNums.length;
  if (n === 0) return null;
  const mid = Math.floor(n / 2);
  if (n % 2 === 1) return sortedNums[mid];
  return (sortedNums[mid - 1] + sortedNums[mid]) / 2;
}

function formatStat(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000 || Number.isInteger(n)) {
    return Math.round(n).toLocaleString('en-US');
  }
  return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

function columnShouldHaveStats(colDef) {
  if (!colDef || colDef.field == null) return false;
  if (STATS_SKIP_FIELDS.has(colDef.field)) return false;
  if (colDef.type === 'actions') return false;
  return colDef.type === 'number';
}

function computeAvgMed(rows, colDef) {
  const nums = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!shouldIncludeRowForStats(row)) continue;
    const v = toFiniteNumber(getValueForStats(row, colDef));
    if (v != null) nums.push(v);
  }
  if (nums.length === 0) return null;
  const sum = nums.reduce((a, b) => a + b, 0);
  const avg = sum / nums.length;
  nums.sort((a, b) => a - b);
  const med = median(nums);
  return { avg, med, n: nums.length };
}

/**
 * @param {import('@mui/x-data-grid-pro').GridColDef[]} columns
 * @param {Record<string, unknown>[]} rows
 * @returns {import('@mui/x-data-grid-pro').GridColDef[]}
 */
export function buildColumnsWithHeaderStats(columns, rows) {
  if (!Array.isArray(columns)) return columns;
  if (columns.length === 0) return columns;
  const rowList = Array.isArray(rows) ? rows : [];

  return columns.map((col) => {
    if (!columnShouldHaveStats(col)) return col;
    const stats = computeAvgMed(rowList, col);
    if (!stats) return col;
    const headerName = col.headerName ?? col.field;
    const description = `${headerName}\nAvg: ${formatStat(stats.avg)}\nMed: ${formatStat(stats.med)}`;
    return { ...col, description };
  });
}
